import { ExternalLink, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip } from '@mui/material';
import { useDispatch, connect } from 'react-redux';
import {
    getAllTestScenariosForEntity,
    getTestScenariosGenerationDetail,
    createTestScenariosGenerationForEntity
} from 'redux-store/aiChatsReducers/aiChatsActions';
import MainLayout from 'layouts/MainLayout';

const ApiTestScenarioGenerator = ({ state, api, projectId, selectedEnvironmentId }) => {
    const dispatch = useDispatch();
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [testScenarios, setTestScenarios] = useState([]);
    const [pendingGenerationId, setPendingGenerationId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [generationError, setGenerationError] = useState('');
    const pollingIntervalRef = useRef(null);
    const dialogContentRef = useRef(null);
    const [totalPages, setTotalPages] = useState(0);

    // Scroll to top when validation error appears
    useEffect(() => {
        if (validationError && dialogContentRef.current) {
            dialogContentRef.current.scrollTop = 0;
        }
    }, [validationError]);
    const [totalScenarios, setTotalScenarios] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);

    // Categories with subcategories based on the provided structure
    const testCategories = [
        {
            id: 'unit',
            label: 'Unit',
            subcategories: []
        },
        {
            id: 'owasp',
            label: 'OWASP',
            subcategories: []
        },
        {
            id: 'penetration',
            label: 'Penetration',
            subcategories: [
                { id: 'bola', label: 'Broken Object Level Authorization (BOLA)' },
                { id: 'bua', label: 'Broken User Authentication (BUA)' },
                { id: 'excessive-data', label: 'Excessive Data Exposure' },
                { id: 'rate-limiting', label: 'Lack of Resources & Rate Limiting' },
                { id: 'broken-function', label: 'Broken Function Level Authorization' },
                { id: 'mass-assignment', label: 'Mass Assignment' },
                { id: 'security-misconfig', label: 'Security Misconfiguration' },
                { id: 'injection', label: 'Injection' },
                { id: 'asset-management', label: 'Improper Assets Management' },
                { id: 'logging-monitoring', label: 'Insufficient Logging & Monitoring' }
            ]
        },
        {
            id: 'security',
            label: 'Security',
            subcategories: [
                { id: 'xss', label: 'Cross-Site Scripting (XSS)' },
                { id: 'bola-security', label: 'Broken Object Level Authorization (BOLA)' },
                { id: 'ssrf', label: 'Server-Side Request Forgery (SSRF)' },
                { id: 'mass-assignment-security', label: 'Mass Assignment (MA)' },
                { id: 'command-injection', label: 'Command Injection' },
                { id: 'cors', label: 'Cross-Origin Resource Sharing (CORS)' },
                { id: 'security-misconfig-security', label: 'Security Misconfiguration (SM)' },
                { id: 'bua-security', label: 'Broken User Authentication (BUA)' },
                { id: 'rate-limiting-security', label: 'Lack of Resources & Rate Limiting (RL)' },
                { id: 'unnecessary-http', label: 'Unnecessary HTTP Methods (UHM)' },
                { id: 'misconfigured-headers', label: 'Misconfigured HTTP Headers (MHH)' },
                { id: 'verbose-errors', label: 'Verbose Error Messages (VEM)' },
                { id: 'lfi', label: 'Local File Inclusion (LFI)' },
                { id: 'ssti', label: 'Server Side Template Injection (SSTI)' },
                { id: 'crlf', label: 'Carriage Return, Line Feed (CRLF) Injection' },
                { id: 'server-version', label: 'Server Version Disclosure (SVD)' },
                { id: 'sql-injection', label: 'SQL Injection' }
            ]
        }
    ];

    const handleCategoryToggle = (categoryId) => {
        const category = testCategories.find(cat => cat.id === categoryId);

        // If category has no subcategories, toggle it directly
        if (!category.subcategories || category.subcategories.length === 0) {
            setSelectedCategories(prev => {
                if (prev.includes(categoryId)) {
                    return prev.filter(id => id !== categoryId);
                } else {
                    return [...prev, categoryId];
                }
            });
            return;
        }

        // If category has subcategories, toggle selection and expand (single expansion)
        setSelectedCategories(prev => {
            if (prev.includes(categoryId)) {
                return prev.filter(id => id !== categoryId);
            } else {
                // Expand the category when selecting it (collapse others)
                setExpandedCategories([categoryId]);
                return [...prev, categoryId];
            }
        });
    };



    const handleSelectAllSubcategories = (categoryId) => {
        const category = testCategories.find(cat => cat.id === categoryId);
        const subcategoryIds = category.subcategories.map(sub => sub.id);

        // Select the parent category
        setSelectedCategories(prev => {
            if (!prev.includes(categoryId)) {
                return [...prev, categoryId];
            }
            return prev;
        });

        setSelectedSubcategories(prev => {
            const newSubcategories = [...prev];
            subcategoryIds.forEach(id => {
                if (!newSubcategories.includes(id)) {
                    newSubcategories.push(id);
                }
            });
            return newSubcategories;
        });
    };

    const handleClearAllSubcategories = (categoryId) => {
        const category = testCategories.find(cat => cat.id === categoryId);
        const subcategoryIds = category.subcategories.map(sub => sub.id);

        // Deselect the parent category
        setSelectedCategories(prev => {
            return prev.filter(id => id !== categoryId);
        });

        setSelectedSubcategories(prev => {
            return prev.filter(id => !subcategoryIds.includes(id));
        });
    };

    const onPageChange = (e, value) => {
        setPageNumber(value);
    };

    const handleExpandToggle = (categoryId) => {
        setExpandedCategories(prev => {
            if (prev.includes(categoryId)) {
                // If already expanded, collapse it
                return [];
            } else {
                // If not expanded, collapse all others and expand this one
                return [categoryId];
            }
        });
    };


    // Polling function
    const startPolling = useCallback((id) => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        pollingIntervalRef.current = setInterval(async () => {
            try {
                // Ensure api is still valid before polling
                if (!api || !api.id) {
                    console.warn('ApiTestScenarioGenerator: api is null during polling, stopping');
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                    return;
                }

                // Fetch generation status
                const statusResponse = await dispatch(getTestScenariosGenerationDetail({
                    generationId: id
                })).unwrap();

                // Only fetch test scenarios if generation is still in progress (not completed or failed)
                if (!statusResponse.completed_at && !statusResponse.last_error) {
                    const scenariosResult = await dispatch(getAllTestScenariosForEntity({
                        projectId: projectId,
                        entityId: api.id,
                        pageNumber: pageNumber
                    })).unwrap();

                    // Update test scenarios if available
                    if (scenariosResult) {
                        if (scenariosResult.test_scenarios) {
                            setTestScenarios(scenariosResult.test_scenarios);
                        }
                        if (typeof scenariosResult.total_pages !== 'undefined') {
                            setTotalPages(scenariosResult.total_pages);
                        }
                        if (typeof scenariosResult.total !== 'undefined') {
                            setTotalScenarios(scenariosResult.total);
                        }
                    }
                }

                // Check if generation is completed
                if (statusResponse.completed_at && !statusResponse.last_error) {
                    // Generation completed successfully
                    setPendingGenerationId(null);
                    setGenerationError('');

                    // Refresh the test scenarios to get the latest data
                    try {
                        const refreshResult = await dispatch(getAllTestScenariosForEntity({
                            projectId: projectId,
                            entityId: api.id,
                            pageNumber: pageNumber
                        })).unwrap();
                        if (refreshResult) {
                            if (refreshResult.test_scenarios) {
                                setTestScenarios(refreshResult.test_scenarios);
                            }
                            if (typeof refreshResult.total_pages !== 'undefined') {
                                setTotalPages(refreshResult.total_pages);
                            }
                            if (typeof refreshResult.total !== 'undefined') {
                                setTotalScenarios(refreshResult.total);
                            }
                        }
                    } catch (refreshError) {
                        console.error('Error refreshing test scenarios:', refreshError);
                    }
                    clearInterval(pollingIntervalRef.current);
                } else if (statusResponse.last_error) {
                    // Generation failed with error
                    setPendingGenerationId(null);
                    setGenerationError(statusResponse.last_error);

                    // Still refresh the test scenarios
                    try {
                        const refreshResult = await dispatch(getAllTestScenariosForEntity({
                            projectId: projectId,
                            entityId: api.id,
                            pageNumber: pageNumber
                        })).unwrap();
                        if (refreshResult) {
                            if (refreshResult.test_scenarios) {
                                setTestScenarios(refreshResult.test_scenarios);
                            }
                            if (typeof refreshResult.total_pages !== 'undefined') {
                                setTotalPages(refreshResult.total_pages);
                            }
                            if (typeof refreshResult.total !== 'undefined') {
                                setTotalScenarios(refreshResult.total);
                            }
                        }
                    } catch (refreshError) {
                        console.error('Error refreshing test scenarios:', refreshError);
                    }
                    clearInterval(pollingIntervalRef.current);
                }
                // If still processing, continue polling
            } catch (error) {
                console.error('Polling error:', error);
                setPendingGenerationId(null);
                clearInterval(pollingIntervalRef.current);
            }
        }, 15000); // Poll every 15 seconds
    }, [dispatch, projectId, api, pageNumber]);

    const handleGenerate = async () => {
        setValidationError('');
        setGenerationError(''); // Clear any previous generation error

        // Check if at least one category is selected
        if (selectedCategories.length === 0) {
            setValidationError('Please select at least one category.');
            return;
        }

        // Check if categories with subcategories have at least one subcategory selected
        const categoriesWithSubcategories = selectedCategories.filter(categoryId => {
            const category = testCategories.find(cat => cat.id === categoryId);
            return category.subcategories && category.subcategories.length > 0;
        });

        const invalidCategories = categoriesWithSubcategories.filter(categoryId => {
            const category = testCategories.find(cat => cat.id === categoryId);
            return !category.subcategories.some(sub => selectedSubcategories.includes(sub.id));
        });

        if (invalidCategories.length > 0) {
            const invalidCategoryNames = invalidCategories.map(id => {
                const category = testCategories.find(cat => cat.id === id);
                return category.label;
            }).join(', ');
            setValidationError(`The category that you have selected (${invalidCategoryNames}) requires at least one subcategory to be selected.`);
            return;
        }

        try {
            setIsModalOpen(false); // Close modal when generation starts

            // Format categories payload
            const categoriesPayload = {};
            testCategories.forEach(category => {
                if (selectedCategories.includes(category.id)) {
                    if (category.subcategories && category.subcategories.length > 0) {
                        // For categories with subcategories, get selected subcategory labels
                        const selectedSubcategoryLabels = category.subcategories
                            .filter(sub => selectedSubcategories.includes(sub.id))
                            .map(sub => sub.label);
                        categoriesPayload[category.label] = selectedSubcategoryLabels;
                    } else {
                        // For categories without subcategories, pass empty array
                        categoriesPayload[category.label] = [];
                    }
                }
            });
            // Ensure api is valid before creating generation
            if (!api || !api.id) {
                setValidationError('API data is not available. Please refresh the page.');
                return;
            }

            const response = await dispatch(createTestScenariosGenerationForEntity({
                entityId: api.id,
                categories: categoriesPayload
            })).unwrap();

            if (response && response.id) {
                setPendingGenerationId(response.id);
                // Start polling for status
                startPolling(response.id);
            }
            // Clear all selections and errors when closing modal
            setSelectedCategories([]);
            setSelectedSubcategories([]);
            setExpandedCategories([]);
            setValidationError('');
        } catch (error) {
            console.error('Error in handleGenerate:', error);
            setValidationError('Failed to start generation. Please try again.');
        }
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        // Clear all selections and errors when closing modal
        setSelectedCategories([]);
        setSelectedSubcategories([]);
        setExpandedCategories([]);
        setValidationError('');
    };


    // Reset state when API changes
    useEffect(() => {
        // Only reset if api is valid and id changes
        if (!api || !api.id) {
            return;
        }

        // Clear all state when API changes
        setTestScenarios([]);
        setPendingGenerationId(null);
        setGenerationError('');
        setPageNumber(1);

        // Clear any existing polling
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, [api]);

    // Fetch test scenarios when component mounts or API/page changes
    useEffect(() => {
        const fetchTestScenarios = async () => {
            // Ensure we have valid api data before proceeding
            if (!projectId || !api || !api.id) {
                console.warn('ApiTestScenarioGenerator: Missing api or api.id, skipping fetch');
                return;
            }

            try {
                setGenerationError(''); // Clear any previous generation error
                const result = await dispatch(getAllTestScenariosForEntity({
                    projectId: projectId,
                    entityId: api.id,
                    pageNumber: pageNumber
                })).unwrap();

                if (result) {
                    if (result.test_scenarios && result.test_scenarios.length > 0) {
                        setTestScenarios(result.test_scenarios);
                    }

                    // Check if there's a pending generation
                    if (result.pending_test_scenario_generation_id) {
                        setPendingGenerationId(result.pending_test_scenario_generation_id);
                        // Start polling for the pending generation
                        startPolling(result.pending_test_scenario_generation_id);
                    }
                    setTotalPages(result.total_pages);
                    setTotalScenarios(result.total);
                }
            } catch (error) {
                console.error('Error fetching test scenarios:', error);
            }
        };

        fetchTestScenarios();
    }, [projectId, api, pageNumber, dispatch, startPolling]);

    // Cleanup polling on component unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    const isCategorySelected = (categoryId) => {
        return selectedCategories.includes(categoryId);
    };



    const isCategoryExpanded = (categoryId) => {
        return expandedCategories.includes(categoryId);
    };


    return (
        <MainLayout
            headerText="Test Scenarios"
            subtitleText={`Showing ${totalScenarios || 0} test scenario(s)`}
            totalPages={totalPages}
            pageNumber={pageNumber}
            onPageChange={onPageChange}
            contentStyle={{ py: 0, px: 0 }}
            rightSideContent={
                <>
                    <button
                        onClick={handleOpenModal}
                        disabled={!!pendingGenerationId}
                        className={`px-6 py-1 mt-1 rounded-1 font-medium text-[var(--text-primary)] transition-colors fs12 disabled:cursor-not-allowed disabled:opacity-50 bgSecondary hoverBg customBorder`}
                    >
                        Generate
                    </button>
                </>
            }
        >
            <div className="max-w-full max-h-[calc(100vh-240px)] overflow-y-auto px-4">
                <Dialog
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        style: {
                            borderRadius: '12px',
                            backgroundColor: 'var(--primary-bg)',
                            color: 'var(--text-primary)',
                            maxHeight: '80vh'
                        }
                    }}
                >
                    <DialogTitle sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--borderColor)',
                        pb: 2
                    }}>
                        Select Test Categories
                    </DialogTitle>

                    <DialogContent
                        ref={dialogContentRef}
                        sx={{
                            pt: 3,
                            maxHeight: '60vh',
                            overflow: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'var(--primary-bg)',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'var(--borderColor)',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                backgroundColor: 'var(--primary-color)',
                            }
                        }}
                    >
                        {validationError && (
                            <Box sx={{
                                my: 2,
                                p: 2,
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                border: '1px solid rgba(244, 67, 54, 0.3)',
                                borderRadius: 1,
                                width: '100%'
                            }}>
                                <Typography variant="body2" sx={{ color: 'var(--error-color)', fontWeight: 500 }}>
                                    {validationError}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{
                            my: 3,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 2
                        }}>
                            {testCategories.map((category) => (
                                <Box key={category.id}>
                                    {/* Main Category */}
                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1,
                                            border: 2,
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            borderColor: isCategorySelected(category.id) ? 'var(--primary-color)' : 'var(--borderColor)',
                                            backgroundColor: isCategorySelected(category.id) ? 'var(--selected-bg)' : 'var(--primary-bg)',
                                            '&:hover': {
                                                backgroundColor: 'var(--background-secondary)'
                                            }
                                        }}
                                        onClick={() => handleCategoryToggle(category.id)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                <Box>
                                                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                                                        {category.label}
                                                        {category.subcategories && category.subcategories.length > 0 && (
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    ml: 1,
                                                                    fontSize: '0.875rem',
                                                                    color: 'var(--text-secondary)',
                                                                    fontWeight: 400
                                                                }}
                                                            >
                                                                ({category.subcategories.filter(sub => selectedSubcategories.includes(sub.id)).length}/{category.subcategories.length})
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {/* Only show expand button if category has subcategories */}
                                            {category.subcategories && category.subcategories.length > 0 && (
                                                <Button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleExpandToggle(category.id);
                                                    }}
                                                    sx={{
                                                        minWidth: 'auto',
                                                        p: 0.5,
                                                        color: 'var(--text-primary)',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }}
                                                >
                                                    {isCategoryExpanded(category.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Expanded Subcategories - shown outside the grid */}
                        {expandedCategories.map((expandedCategoryId) => {
                            const category = testCategories.find(cat => cat.id === expandedCategoryId);
                            if (!category || !category.subcategories || category.subcategories.length === 0) return null;

                            return (
                                <Box key={`expanded-${expandedCategoryId}`} sx={{ mt: 3 }}>
                                    {/* Select All / Clear All buttons */}
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        <Button
                                            onClick={() => handleSelectAllSubcategories(expandedCategoryId)}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontSize: '0.75rem',
                                                py: 0.5,
                                                px: 1,
                                                color: 'var(--text-primary)',
                                                borderColor: 'var(--borderColor)',
                                                '&:hover': {
                                                    borderColor: 'var(--primary-color)',
                                                    backgroundColor: 'var(--background-secondary)'
                                                }
                                            }}
                                        >
                                            Select All ({category.subcategories.length})
                                        </Button>
                                        <Button
                                            onClick={() => handleClearAllSubcategories(expandedCategoryId)}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontSize: '0.75rem',
                                                py: 0.5,
                                                px: 1,
                                                color: 'var(--text-primary)',
                                                borderColor: 'var(--borderColor)',
                                                '&:hover': {
                                                    borderColor: 'var(--primary-color)',
                                                    backgroundColor: 'var(--background-secondary)'
                                                }
                                            }}
                                        >
                                            Clear All
                                        </Button>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {category.subcategories.map((subcategory) => {
                                            const isSelected = selectedSubcategories.includes(subcategory.id);
                                            return (
                                                <Box
                                                    key={subcategory.id}
                                                    onClick={() => {
                                                        setSelectedSubcategories((prev) => {
                                                            if (isSelected) {
                                                                return prev.filter((id) => id !== subcategory.id);
                                                            } else {
                                                                return [...prev, subcategory.id];
                                                            }
                                                        });
                                                        setSelectedCategories((prev) => {
                                                            if (!isSelected && !prev.includes(expandedCategoryId)) {
                                                                return [...prev, expandedCategoryId];
                                                            }
                                                            return prev;
                                                        });
                                                    }}
                                                    sx={{
                                                        px: 2,
                                                        py: 0.5,
                                                        borderRadius: 2,
                                                        border: '1px solid',
                                                        borderColor: isSelected ? 'var(--primary-color)' : 'var(--borderColor)',
                                                        backgroundColor: isSelected ? 'var(--primary-bg)' : 'var(--primary-bg)',
                                                        color: isSelected ? 'var(--text-secondary)' : 'var(--text-primary)',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        display: 'inline-block',
                                                        whiteSpace: 'nowrap',
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        '&:hover': {
                                                            borderColor: 'var(--primary-color)',
                                                            backgroundColor: 'var(--background-secondary)'
                                                        }
                                                    }}
                                                >
                                                    {subcategory.label}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            );
                        })}
                    </DialogContent>

                    <DialogActions sx={{ p: 3, borderTop: '1px solid var(--borderColor)' }}>
                        <Button
                            onClick={handleCloseModal}
                            variant="outlined"
                            sx={{
                                color: 'var(--text-primary)',
                                borderColor: 'var(--borderColor)',
                                '&:hover': {
                                    borderColor: 'var(--primary-color)'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleGenerate}
                            disabled={selectedCategories.length === 0}
                            variant="contained"
                            sx={{
                                backgroundColor: 'var(--primary-color)',
                                '&:hover': {
                                    backgroundColor: 'var(--primary-color)',
                                    opacity: 0.9
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: 'var(--background-primary)',
                                    color: 'var(--text-secondary)'
                                }
                            }}
                        >
                            Generate Test Scenarios
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Processing State */}
                {pendingGenerationId && (
                    <div className="rounded-2 px-8 py-3 text-center mt-6 bg-[#1565c01a]" style={{ border: "1px solid #1565c0" }}>
                        <div className='w-full flex items-center justify-start gap-2'>
                            <Loader2 className="inline-block animate-spin rounded-full h-6 w-6 text-[#1565c0]" />
                            <p className="fs14 font-normal mb-0" style={{ color: 'var(--text-primary)' }}>
                                Generating Test Scenarios...
                            </p>
                        </div>
                    </div>
                )}

                {/* Generation Error State */}
                {generationError && (
                    <div className="rounded-2 p-4 mt-6" style={{
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        border: '1px solid rgba(244, 67, 54, 0.3)'
                    }}>
                        <div className='flex items-center gap-2'>
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'red' }}></div>
                            <h3 className="fs14 font-semibold mb-0">
                                Generation Failed
                            </h3>
                        </div>
                        <p className="fs12 mt-2 mb-0" style={{ color: 'var(--text-primary)' }}>
                            {generationError}
                        </p>
                    </div>
                )}

                {/* Completed State */}
                <div className="rounded-2 bgPrimary">
                    {/* Display Generated Scenarios */}
                    <div>
                        <TableContainer
                            component={Paper}
                            sx={{
                                backgroundColor: 'var(--primary-bg)',
                                border: "1px solid var(--borderColor)",
                                mt: 2,
                                // maxHeight: 'calc(100vh - 290px)',
                                // overflow: 'auto'
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'var(--background-secondary)' }}>
                                        <TableCell
                                            sx={{
                                                fontWeight: '400',
                                                color: 'var(--text-primary)',
                                                borderBottom: '1px solid var(--borderColor)',
                                                py: 0.5,
                                                px: 2,
                                                width: "90%"
                                            }}
                                        >
                                            Detail
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: '400',
                                                color: 'var(--text-primary)',
                                                borderBottom: '1px solid var(--borderColor)',
                                                py: 0.5,
                                                px: 2
                                            }}
                                        >
                                            Action
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {testScenarios && testScenarios.length > 0 ? (
                                        testScenarios.map((scenario) => (
                                            <TableRow
                                                key={scenario.id}
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: 'var(--background-secondary)'
                                                    }
                                                }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        color: 'var(--text-primary)',
                                                        borderBottom: '1px solid var(--borderColor)',
                                                        py: 1,
                                                        px: 2
                                                    }}
                                                >
                                                    <div>
                                                        <Typography variant="body1" sx={{
                                                            fontWeight: 500,
                                                            lineHeight: 1.5,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                        }}>
                                                            {(() => {
                                                                const environmentStatus = scenario.test_scenario_statuses?.find(
                                                                    status => status.environment_set?.id === selectedEnvironmentId
                                                                );
                                                                return environmentStatus ? (
                                                                    <Tooltip title={environmentStatus.status} placement='bottom'>
                                                                        <span
                                                                            style={{
                                                                                display: 'inline-block',
                                                                                width: '8px',
                                                                                height: '8px',
                                                                                borderRadius: '50%',
                                                                                backgroundColor: environmentStatus.status === 'fail' ? '#f44336' : '#4caf50',
                                                                                marginRight: '8px',
                                                                                flexShrink: 0,
                                                                                cursor: "pointer"
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                ) : null;
                                                            })()}
                                                            {scenario.name || scenario.title}
                                                        </Typography>
                                                        {scenario.tags && scenario.tags.length > 0 && (
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                                                {scenario.tags.map((tag, index) => (
                                                                    <Chip
                                                                        key={index}
                                                                        label={tag}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: 'var(--background-secondary)',
                                                                            color: 'var(--text-primary)',
                                                                            fontSize: '0.75rem',
                                                                            border: '1px solid var(--borderColor)',
                                                                            borderRadius: "4px"
                                                                        }}
                                                                    />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        color: 'var(--text-primary)',
                                                        borderBottom: '1px solid var(--borderColor)',
                                                        py: 1,
                                                        px: 2
                                                    }}
                                                >
                                                    <a
                                                        href={`/test-scenarios/${scenario.id}`}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className='px-2 py-1.5 rounded-1 hover:bg-[var(--primary-color)] inline-flex items-center gap-2 transition-colors duration-300'
                                                        style={{ border: "1px solid var(--primary-color)" }}
                                                    >
                                                        <ExternalLink size={12} className='text-[var(--text-primary)]' />
                                                        {/* <span className='fs12 text-[var(--text-primary)]'>View</span> */}
                                                    </a>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={2}
                                                sx={{
                                                    textAlign: 'center',
                                                    color: 'var(--text-secondary)',
                                                    py: 4,
                                                    borderBottom: 'none'
                                                }}
                                            >
                                                <Typography variant="body1">
                                                    No Scenarios Found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                </div>
            </div>
        </MainLayout >
    );
};

const mapStateToProps = (state) => {
    return {
        projectId: state.common.selectedProject?.id,
        selectedEnvironmentId: state?.variableSet?.selectedEnvironmentVariables?.id
    };
};

export default connect(mapStateToProps)(ApiTestScenarioGenerator);
